import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Banknote,
  CalendarDays,
  Calculator,
  CheckCircle2,
  Info,
  Inbox,
  Lock,
  MessageSquare,
  Undo2,
  Wallet,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { CierreDeCaja, PreviaDelCierre } from '@/shared/api/tipos';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';

import {
  BILLETES,
  MONEDAS,
  type Conteo,
  type Pieza,
  importeDe,
  loContado,
  totalContado,
} from './piezas';
import { repartoPorConcepto } from './estados';

const NOMBRE_DEL_MEDIO: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  deposito: 'Depósito',
};

export function ModalDeCierre({
  previa,
  alCerrado,
  alCancelar,
}: {
  previa: PreviaDelCierre;
  alCerrado: (cierre: CierreDeCaja) => void;
  alCancelar: () => void;
}) {
  const [conteo, setConteo] = useState<Conteo>({});
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clienteDeConsultas = useQueryClient();

  const contado = useMemo(() => totalContado(conteo), [conteo]);

  const p = previa;

  const fondoNumero = p.fondoDeCaja ?? 0;

  const diferencia = Math.round((contado - fondoNumero - p.esperadoEfectivo) * 100) / 100;

  const cerrar = useMutation({
    mutationFn: () =>
      api.post<CierreDeCaja>('/cierres', {
        fecha: p.fecha,
        conteo: loContado(conteo),
        motivoDiferencia: motivo.trim() || undefined,
        observaciones: observaciones.trim() || undefined,
      }),
    onSuccess: (cierre) => {
      notificar.exito('Caja cerrada');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cierre-previa'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['jornada-de-caja'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cierres'] });
      alCerrado(cierre);
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo cerrar la caja';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const faltaMotivo = diferencia !== 0 && motivo.trim().length < 10;

  function contar(pieza: Pieza, piezas: string) {
    setConteo({ ...conteo, [pieza.clave]: piezas });
    setError(null);
  }

  return (
    <Modal
      abierto
      alCerrar={alCancelar}
      titulo="Cierre de caja"
      subtitulo={`${p.nombreDelCajero}${p.caja ? ` · Caja ${p.caja}` : ''}`}
      icono={Lock}
      ancho="pantalla"

      cerrarAlPulsarFuera={false}
    >

      <div className="grid items-start gap-4 xl:grid-cols-[17rem_minmax(0,1fr)_20rem]">

        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-2xl bg-armazon-900 p-4 text-white shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60">
              Debe haber en el cajón
            </p>
            <p className="importe mt-1 text-3xl font-bold">${p.esperadoEfectivo.toFixed(2)}</p>
            <p className="mt-1 text-[11px] uppercase text-white/60">
              {p.cuantosCobros} {p.cuantosCobros === 1 ? 'cobro' : 'cobros'} en efectivo
            </p>
            <Inbox className="absolute -bottom-2 right-3 size-14 text-white/10" aria-hidden />
          </div>

          <div className="rounded-2xl border border-armazon-200 bg-white p-4">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
              <CalendarDays className="size-3.5" aria-hidden />
              La jornada entera
            </p>
            <ul className="mt-2.5 space-y-1.5">
              {Object.entries(p.porMetodo).map(([codigo, cuanto]) => (
                <li key={codigo} className="flex items-center justify-between gap-3">
                  <span className="text-xs uppercase text-armazon-600">
                    {NOMBRE_DEL_MEDIO[codigo] ?? codigo}
                  </span>
                  <span className="importe text-sm font-bold text-marca-700">
                    ${cuanto.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
            {repartoPorConcepto(p.conceptos).length > 0 && (
              <ul className="mt-2.5 space-y-1.5 border-t border-armazon-100 pt-2.5">
                {repartoPorConcepto(p.conceptos).map(([nombre, cuanto]) => (
                  <li key={nombre} className="flex items-center justify-between gap-3">
                    <span className="text-xs uppercase text-armazon-600">{nombre}</span>
                    <span className="importe text-sm font-bold text-armazon-800">
                      ${cuanto.toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {p.esperadoOtros > 0 && (
              <p className="mt-2.5 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
                Los ${p.esperadoOtros.toFixed(2)} que no son efectivo no están en el cajón: se
                cuadran contra el extracto del banco.
              </p>
            )}
          </div>

          {p.cuantosAnulados > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                <Undo2 className="size-3.5" aria-hidden />
                Anulado durante la jornada
              </p>
              <p className="importe mt-1 text-2xl font-bold text-amber-900">
                ${p.anuladoTotal.toFixed(2)}
              </p>
              <p className="mt-1 text-justify text-[10px] uppercase leading-relaxed text-amber-800">
                {p.cuantosAnulados}{' '}
                {p.cuantosAnulados === 1 ? 'cobro deshecho' : 'cobros deshechos'}. Ese dinero no
                está en el cajón y ya está descontado de lo que debe haber.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-armazon-200 bg-white p-4 shadow-sm">
          <p className="flex items-center gap-2 border-b border-armazon-100 pb-2.5 text-sm font-bold uppercase text-armazon-800">
            <Banknote className="size-4 text-armazon-500" aria-hidden />
            Cuente el cajón
          </p>

          <div className="grid gap-5 pt-3 sm:grid-cols-2">
            <ColumnaDePiezas
              titulo="Billetes"
              subtitulo="billete"
              piezas={BILLETES}
              conteo={conteo}
              alContar={contar}
            />

            <div className="sm:border-l sm:border-armazon-100 sm:pl-5">
              <ColumnaDePiezas
                titulo="Monedas"
                subtitulo="moneda"
                piezas={MONEDAS}
                conteo={conteo}
                alContar={contar}
              />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-2xl border border-armazon-200 bg-armazon-50/50 p-4">
            <p className="flex items-center gap-2 border-b border-armazon-200 pb-2.5 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
              <Calculator className="size-3.5" aria-hidden />
              Resumen del conteo
            </p>

            <dl className="mt-3 space-y-2.5">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-[11px] font-semibold uppercase text-armazon-600">Contado</dt>
                <dd className="importe text-lg font-bold text-armazon-900">
                  ${contado.toFixed(2)}
                </dd>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-dashed border-armazon-200 pt-2.5">
                <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-armazon-600">
                  <Wallet className="size-3.5" aria-hidden />
                  Fondo de caja
                </dt>
                <dd className="importe text-lg font-bold text-armazon-900">
                  ${fondoNumero.toFixed(2)}
                </dd>
              </div>
            </dl>

            <p className="mt-1.5 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
              El fondo es el que declaró al abrir la caja. Se descuenta del conteo y no se puede
              cambiar aquí.
            </p>

            <div className="mt-3">
              <Diferencia diferencia={diferencia} grande />
            </div>
          </div>

          {diferencia !== 0 && (
            <label className="block rounded-2xl border border-armazon-200 bg-white p-4">
              <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
                <AlertCircle className="size-3.5" aria-hidden />
                Por qué no cuadra <span className="text-alerta">*</span>
              </span>
              <textarea
                value={motivo}
                onChange={(evento) => {
                  setMotivo(evento.target.value);
                  setError(null);
                }}
                rows={3}
                maxLength={500}
                placeholder="Se dio un cambio de más en el último cobro de la tarde."
                className="mt-2 w-full rounded-lg border border-armazon-200 px-3 py-2 text-sm outline-none transition-colors focus:border-marca-400"
              />
              <span className="mt-1.5 block text-justify text-[11px] leading-snug text-armazon-500">
                Se puede cerrar sin cuadrar, pero no en silencio. Quien lo lea dentro de seis meses
                no estuvo aquí.
              </span>
            </label>
          )}

          <label className="block rounded-2xl border border-armazon-200 bg-white p-4">
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-700">
              <MessageSquare className="size-3.5" aria-hidden />
              Observaciones
            </span>
            <textarea
              value={observaciones}
              onChange={(evento) => setObservaciones(evento.target.value)}
              rows={2}
              maxLength={500}
              className="mt-2 w-full rounded-lg border border-armazon-200 px-3 py-2 text-sm outline-none transition-colors focus:border-marca-400"
            />
          </label>
        </div>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-armazon-100 pt-4">
        <p className="flex min-w-0 flex-1 items-center gap-3 text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
          <Info className="size-5 shrink-0 text-blue-600" aria-hidden />
          <span>
            Al cerrar, su caja no admite más cobros hoy. Si queda alguien por atender, atiéndalo
            antes.
          </span>
        </p>

        <button
          type="button"
          onClick={() => {
            setError(null);
            cerrar.mutate();
          }}
          disabled={faltaMotivo || cerrar.isPending}
          className="flex h-12 shrink-0 items-center justify-center gap-3 rounded-xl bg-marca-600 px-8 text-sm font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-55"
        >
          <Lock className="size-5" aria-hidden />
          {cerrar.isPending ? 'Cerrando…' : 'Cerrar la caja'}
        </button>
      </div>
    </Modal>
  );
}

function ColumnaDePiezas({
  titulo,
  subtitulo,
  piezas,
  conteo,
  alContar,
}: {
  titulo: string;
  subtitulo: string;
  piezas: readonly Pieza[];
  conteo: Conteo;
  alContar: (pieza: Pieza, piezas: string) => void;
}) {
  return (
    <div>
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-armazon-400">
        {titulo}
      </p>
      <div className="space-y-2.5">
        {piezas.map((pieza) => (
          <label key={pieza.clave} className="flex items-center gap-3">
            <span className="w-12 shrink-0">
              <span className="importe block text-sm font-bold text-armazon-800">
                {pieza.etiqueta}
              </span>
              <span className="block text-[9px] uppercase text-armazon-400">{subtitulo}</span>
            </span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={conteo[pieza.clave] ?? ''}
              onChange={(evento) => alContar(pieza, evento.target.value)}
              placeholder="0"
              className="importe h-10 w-full min-w-0 flex-1 rounded-lg border border-armazon-200 px-2 text-center text-sm outline-none transition-colors focus:border-marca-400"
            />
            <span className="importe w-16 shrink-0 text-right text-xs text-armazon-500">
              ${importeDe(conteo, pieza).toFixed(2)}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Diferencia({ diferencia, grande }: { diferencia: number; grande?: boolean }) {
  const cuadra = diferencia === 0;
  const sobra = diferencia > 0;

  const marco = cuadra
    ? 'border-marca-200 bg-marca-50'
    : sobra
      ? 'border-blue-200 bg-blue-50'
      : 'border-red-200 bg-red-50';
  const rotulo = cuadra ? 'text-marca-800' : sobra ? 'text-blue-800' : 'text-red-800';
  const cifra = cuadra ? 'text-marca-700' : sobra ? 'text-blue-900' : 'text-red-900';

  return (
    <div className={`rounded-xl border p-4 ${marco}`}>
      <p
        className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide ${rotulo}`}
      >
        {cuadra ? (
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
        ) : (
          <AlertCircle className="size-4 shrink-0" aria-hidden />
        )}
        {cuadra ? 'La caja cuadra' : sobra ? 'Sobra en el cajón' : 'Falta en el cajón'}
      </p>
      <p className={`importe mt-1 font-bold ${cifra} ${grande ? 'text-3xl' : 'text-2xl'}`}>
        ${Math.abs(diferencia).toFixed(2)}
      </p>
    </div>
  );
}
